/**
 * Simulador API client — typed wrappers around `supabase/functions/simulator`.
 *
 * Clerk JWT is required for all routes except /health. Caller passes the
 * token (usually from `useAuth().getToken()`).
 */

import { isNoConfiguredProviderResponse, NoConfiguredProviderError } from "@/lib/edgeFnError";
import type {
  AchievementsResponse,
  EndResponse,
  HeatmapRow,
  LeaderboardResponse,
  ReplyResponse,
  ReportShape,
  ResumeResponse,
  SimulatorDifficulty,
  StartResponse,
} from "../types";

function fnUrl(path: string): string {
  const base = import.meta.env.VITE_SUPABASE_URL as string;
  return `${base}/functions/v1/simulator${path}`;
}

async function readError(res: Response, fallback: string): Promise<Error> {
  let message = "";
  let code = "";
  let parsed: { error?: string; detail?: string; message?: string; code?: string } | null = null;
  try {
    parsed = (await res.json()) as {
      error?: string;
      detail?: string;
      message?: string;
      code?: string;
    };
    code = parsed.error ?? parsed.code ?? "";
    message = parsed.detail ?? parsed.message ?? parsed.error ?? parsed.code ?? "";
  } catch {
    message = res.statusText;
  }

  if (isNoConfiguredProviderResponse(res.status, parsed)) {
    return new NoConfiguredProviderError(message || code || "no_configured_provider", {
      status: res.status,
      code,
    });
  }

  const err = new Error(message || code || fallback);
  (err as Error & { status?: number; code?: string }).status = res.status;
  (err as Error & { status?: number; code?: string }).code = code;
  return err;
}

async function postJson<T>(path: string, body: unknown, token: string): Promise<T> {
  const res = await fetch(fnUrl(path), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) {
    throw await readError(res, `simulator_${path}_failed`);
  }
  return (await res.json()) as T;
}

async function getJson<T>(path: string, token: string): Promise<T> {
  const res = await fetch(fnUrl(path), {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw await readError(res, `simulator_${path}_failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

export async function startSession(
  args: {
    eneatype: number;
    difficulty: SimulatorDifficulty;
    /** Optional: pin a specific procedure. When omitted, server picks at random. */
    procedure_id?: string | null;
    idempotency_key?: string;
  },
  token: string,
): Promise<StartResponse> {
  return await postJson<StartResponse>("/start", args, token);
}

export type ReplayResponse =
  | StartResponse
  | (Omit<StartResponse, "first_message"> & { first_message?: undefined });

export async function replayFromTurn(
  args: { parent_session_id: string; replay_from_seq?: number },
  token: string,
): Promise<ReplayResponse> {
  return await postJson<ReplayResponse>("/replay", args, token);
}

export async function replyTurn(
  args: { session_id: string; content: string; client_seq?: number; audio_url?: string },
  token: string,
): Promise<ReplyResponse> {
  return await postJson<ReplyResponse>("/reply", args, token);
}

export async function resumeSession(
  args: { session_id: string; since_seq?: number },
  token: string,
): Promise<ResumeResponse> {
  return await postJson<ResumeResponse>("/resume", args, token);
}

export async function endSession(
  args: { session_id: string; reason?: "user_quit" | "timeout" | "admin" },
  token: string,
): Promise<EndResponse> {
  return await postJson<EndResponse>("/end", args, token);
}

export type TranscriptMessage = { seq: number; role: "user" | "patient" | "hint"; content: string };

export async function getReport(
  session_id: string,
  token: string,
): Promise<{ report: ReportShape; transcript?: TranscriptMessage[] }> {
  return await postJson<{ report: ReportShape; transcript?: TranscriptMessage[] }>("/report/get", { session_id }, token);
}

export async function transcribeAudio(
  blob: Blob,
  filename: string,
  token: string,
): Promise<{ transcript: string }> {
  const form = new FormData();
  form.append("file", blob, filename);
  const res = await fetch(fnUrl("/transcribe"), {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) {
    let detail = "";
    let code = "";
    let parsed: { error?: string; detail?: string; message?: string } | null = null;
    try {
      parsed = (await res.json()) as { error?: string; detail?: string; message?: string };
      code = parsed.error ?? "";
      detail = parsed.message ?? parsed.detail ?? parsed.error ?? "";
    } catch {
      detail = res.statusText;
    }
    if (isNoConfiguredProviderResponse(res.status, parsed)) {
      throw new NoConfiguredProviderError(detail || code || "no_configured_provider", {
        status: res.status,
        code,
      });
    }
    const err = new Error(detail || code || "transcribe_failed");
    (err as Error & { status?: number; code?: string }).status = res.status;
    (err as Error & { status?: number; code?: string }).code = code;
    throw err;
  }
  return (await res.json()) as { transcript: string };
}

export interface HintResponse {
  hint: string;
  penalty: number;
  hint_penalty_total: number;
  seq: number;
}

export async function requestHint(
  session_id: string,
  token: string,
): Promise<HintResponse> {
  return await postJson<HintResponse>("/hint", { session_id }, token);
}

export async function getAchievements(token: string): Promise<AchievementsResponse> {
  return await getJson<AchievementsResponse>("/achievements", token);
}

export async function getHeatmap(token: string): Promise<{ heatmap: HeatmapRow[] }> {
  return await getJson<{ heatmap: HeatmapRow[] }>("/heatmap", token);
}

export async function getLeaderboard(token: string): Promise<LeaderboardResponse> {
  return await getJson<LeaderboardResponse>("/leaderboard", token);
}

/** Find the active session for the current user (returns null if none). */
export async function findActiveSession(
  // deno-lint-ignore no-explicit-any
  supabase: any,
): Promise<string | null> {
  const { data } = await supabase
    .from("simulator_sessions")
    .select("id")
    .in("status", ["active", "paused"])
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  return data?.id ?? null;
}
